/**
 * TimedProgress — thin progress bar for operations with known-ish duration
 * but no streaming feedback (Claude, Gemini, Veo). Grows to 95% over
 * `expectedMs`, holds there until unmounted; on unmount the parent has
 * either replaced it (success) or swapped to an error state.
 */
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface Props {
  expectedMs: number;
  label?: string;
  startedAt?: number;
  className?: string;
  tone?: 'violet' | 'amber' | 'emerald';
}

const TONE_CLASS: Record<NonNullable<Props['tone']>, string> = {
  violet: 'bg-violet-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
};

export function TimedProgress({ expectedMs, label, startedAt, className, tone = 'violet' }: Props) {
  const [now, setNow] = useState(() => Date.now());
  const t0 = startedAt ?? now;

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);

  const elapsed = Math.max(0, now - t0);
  const raw = Math.min(elapsed / expectedMs, 1);
  // Ease so it slows down near 95%
  const eased = raw < 1 ? raw * 0.95 : 0.95 + (1 - Math.exp(-(elapsed - expectedMs) / 8000)) * 0.05;
  const pct = Math.min(eased, 0.99);
  const secs = (elapsed / 1000).toFixed(0);
  const expectedS = Math.round(expectedMs / 1000);
  const overrun = elapsed > expectedMs;

  return (
    <div className={cn('space-y-0.5', className)}>
      {label && (
        <div className="flex items-center justify-between text-2xs text-muted-foreground">
          <span>{label}</span>
          <span className={cn('tabular-nums', overrun ? 'text-amber-500' : 'text-muted-foreground/70')}>
            {secs}s{overrun ? ` (expected ~${expectedS}s)` : ` / ~${expectedS}s`}
          </span>
        </div>
      )}
      <div className="h-1 w-full overflow-hidden rounded-full bg-muted/40">
        <div
          className={cn('h-full transition-[width] duration-200 ease-out', TONE_CLASS[tone])}
          style={{ width: `${(pct * 100).toFixed(1)}%` }}
        />
      </div>
    </div>
  );
}
